import { AccountBalanceOutlined, AddOutlined, ArrowBackOutlined, VerifiedOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Divider, IconButton, Paper, Stack, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FeedbackSnackbar, InformationPage, PageSections, ResourceState } from '../../../design-system';
import { AffiliateLegalDetail } from '../components/AffiliateLegalDetail';
import { AffiliateCreateDialog } from '../components/AffiliateCreateDialog';
import { SettingRow } from '../components/SettingRow';
import { emptyCompanyCreateInput } from '../fixtures/tenantSettingsFixtures';
import {
  characterLength,
  companyCreateProfile,
  normalizeCompanyLegalProfile,
  validCompanyLegalProfile,
} from '../model/companyLegal';
import { errorCode } from '../model/resource';
import type { CompanyCreateInput, TenantSettingsPageProps } from '../model/types';
import { useCompanyWorkspace } from '../model/useCompanyWorkspace';
import { useTenantSettingsRoute } from '../model/useTenantSettingsRoute';
export function AffiliatesPage({ port, route, fixtureMode, access }: TenantSettingsPageProps) {
  const location = useTenantSettingsRoute('admin-affiliates', route);
  const canWrite =
    access.scopes.includes('tenant.settings.read') &&
    !access.readOnly &&
    access.scopes.includes('tenant.settings.write');
  const canReadSeal = access.scopes.includes('certificates.settings.read');
  const profiles = useCompanyWorkspace(port, fixtureMode);
  const { draft, baseline, dirty, setDraft, setBaseline } = profiles;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState<CompanyCreateInput>(emptyCompanyCreateInput);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string>();
  const selected = profiles.companies.find((profile) => profile.companyId === location.companyId);
  useEffect(() => {
    if (location.view !== 'company-legal' || !selected) return;
    if (draft?.companyId === selected.companyId) {
      if (!dirty && baseline?.version !== selected.version) {
        setDraft(selected);
        setBaseline(selected);
      }
      return;
    }
    if (!dirty) {
      setDraft(selected);
      setBaseline(selected);
      setEditing(false);
    }
  }, [baseline?.version, dirty, draft?.companyId, location.view, selected]);
  useEffect(() => {
    if (location.view !== 'company-legal' || profiles.resource.loading || !profiles.ready || selected) return;
    route.navigate('admin-affiliates', 'companies', '', {
      replace: true,
    });
  }, [location.view, profiles.ready, profiles.resource.loading, route, selected]);
  const displayed = useMemo(
    () => profiles.companies.map((profile) => (dirty && draft?.companyId === profile.companyId ? draft : profile)),
    [draft, dirty, profiles.companies],
  );
  const affiliates = displayed.filter((profile) => !profile.isPrimary);
  const valid = validCompanyLegalProfile(draft);
  const createProfile = companyCreateProfile(createDraft);
  const createValid =
    /^[A-Z][A-Z0-9._-]{0,63}$/.test(createDraft.companyCode.trim().toUpperCase()) &&
    characterLength(createDraft.descriptor) >= 1 &&
    characterLength(createDraft.descriptor) <= 120 &&
    /^[A-Z]{2}$/.test(createDraft.countryCode.trim().toUpperCase()) &&
    validCompanyLegalProfile(createProfile);
  const save = () => {
    if (!draft || !dirty || !valid || !canWrite || saving) return;
    setSaving(true);
    setError(undefined);
    setNotice(undefined);
    const normalizedDraft = normalizeCompanyLegalProfile({
      ...draft,
      descriptor: draft.descriptor.trim(),
      legalName: draft.legalName.trim(),
      mainPhoneNumber: draft.mainPhoneNumber.trim(),
      registeredAddress: draft.registeredAddress.trim(),
      representativeTitle: draft.representativeTitle.trim(),
      representativeName: draft.representativeName.trim(),
    });
    const request = fixtureMode
      ? Promise.resolve({
          ...normalizedDraft,
          version: normalizedDraft.version + 1,
          updatedAt: new Date().toISOString(),
        })
      : port.updateCompanyLegalProfile(normalizedDraft.companyId, normalizedDraft);
    void request
      .then(
        (profile) => {
          const normalized = normalizeCompanyLegalProfile(profile);
          setDraft(normalized);
          setBaseline(normalized);
          profiles.replace(normalized);
          setEditing(false);
          setNotice(`${normalized.descriptor} 법인 정보를 저장했습니다.`);
        },
        (reason: unknown) => {
          if (errorCode(reason) === 'VERSION_CONFLICT') {
            setError('다른 사용자가 이 법인 정보를 먼저 변경했습니다. 최신 값을 다시 확인해 주세요.');
            setDraft(undefined);
            setBaseline(undefined);
            profiles.resource.reload();
          } else if (errorCode(reason) === 'REGISTRATION_NUMBER_CONFLICT')
            setError('입력한 등록번호가 다른 법인에 이미 등록되어 있습니다.');
          else setError(reason instanceof Error ? reason.message : '법인 정보를 저장하지 못했습니다.');
        },
      )
      .finally(() => setSaving(false));
  };
  const create = () => {
    if (!canWrite || creating || !createValid) return;
    setCreating(true);
    setCreateError(undefined);
    const input: CompanyCreateInput = {
      ...createDraft,
      companyCode: createDraft.companyCode.trim().toUpperCase(),
      descriptor: createDraft.descriptor.trim(),
      countryCode: createDraft.countryCode.trim().toUpperCase(),
      legalName: createDraft.legalName.trim(),
      mainPhoneNumber: createDraft.mainPhoneNumber.trim(),
      registeredAddress: createDraft.registeredAddress.trim(),
      representativeTitle: createDraft.representativeTitle.trim(),
      representativeName: createDraft.representativeName.trim(),
    };
    const request = fixtureMode
      ? Promise.resolve({
          ...companyCreateProfile(input),
          companyId: crypto.randomUUID().replaceAll('-', ''),
          version: 1,
          updatedAt: new Date().toISOString(),
        })
      : port.createCompany(input, crypto.randomUUID());
    void request
      .then(
        (profile) => {
          const normalized = normalizeCompanyLegalProfile(profile);
          profiles.add(normalized);
          setCreateOpen(false);
          setCreateDraft(emptyCompanyCreateInput);
          setNotice(`${normalized.descriptor} 계열사를 추가했습니다.`);
        },
        (reason: unknown) => {
          if (errorCode(reason) === 'COMPANY_CODE_CONFLICT')
            setCreateError('이미 사용 중인 회사 코드입니다. 다른 코드를 입력해 주세요.');
          else if (errorCode(reason) === 'REGISTRATION_NUMBER_CONFLICT')
            setCreateError('입력한 등록번호가 다른 법인에 이미 등록되어 있습니다.');
          else if (errorCode(reason) === 'IDEMPOTENCY_CONFLICT')
            setCreateError('회사 추가 요청이 이전 요청과 일치하지 않습니다. 창을 닫고 다시 시도해 주세요.');
          else setCreateError(reason instanceof Error ? reason.message : '계열사를 추가하지 못했습니다.');
        },
      )
      .finally(() => setCreating(false));
  };
  const detail = location.view === 'company-legal';
  return (
    <>
      <InformationPage
        title={detail ? (selected ? `${selected.descriptor} 법인 정보` : '법인 정보') : '계열사 관리'}
        titleRef={location.titleRef}
        width="compact"
        navigation={
          detail ? (
            <Tooltip title="계열사 관리로 돌아가기">
              <span>
                <IconButton
                  aria-label="계열사 관리로 돌아가기"
                  onClick={() => location.navigate('companies')}
                  disabled={saving}
                >
                  <ArrowBackOutlined />
                </IconButton>
              </span>
            </Tooltip>
          ) : undefined
        }
        primaryAction={
          !detail && canWrite ? (
            <Button
              variant="fill"
              startIcon={<AddOutlined />}
              onClick={() => {
                setCreateDraft(emptyCompanyCreateInput);
                setCreateError(undefined);
                setNotice(undefined);
                setCreateOpen(true);
              }}
              disabled={saving}
            >
              계열사 추가
            </Button>
          ) : undefined
        }
      >
        <Box>
          {detail ? (
            <AffiliateLegalDetail
              canWrite={canWrite}
              canReadSeal={canReadSeal}
              error={error}
              resource={profiles.resource}
              draft={draft}
              dirty={dirty}
              valid={valid}
              editing={editing}
              saving={saving}
              companyId={location.companyId}
              onCancel={() => {
                if (baseline) setDraft(baseline);
                setEditing(false);
                setError(undefined);
                setNotice(undefined);
              }}
              onEdit={() => {
                setEditing(true);
                setError(undefined);
                setNotice(undefined);
              }}
              onSave={save}
              onChange={(field, value) => {
                setDraft((current) => (current ? { ...current, [field]: value } : current));
                setNotice(undefined);
              }}
              onOpenSeal={() => {
                if (draft) route.navigate('admin-company-seals', 'company-seal', draft.companyId);
              }}
            />
          ) : (
            <Box data-testid="company-list-content"><PageSections>
              {dirty ? (
                <Alert severity="info">
                  {draft?.descriptor}의 저장하지 않은 변경이 있습니다. 저장한 뒤 다른 회사를 수정할 수 있습니다.
                </Alert>
              ) : null}
              <ResourceState
                loading={profiles.resource.loading}
                error={profiles.resource.error?.message}
                empty={profiles.ready && affiliates.length === 0}
                onRetry={profiles.resource.reload}
                loadingLabel="계열사 목록을 불러오는 중입니다."
                retryLabel="계열사 목록 다시 시도"
                emptyTitle="등록된 계열사가 없습니다."
                emptyDescription={
                  canWrite
                    ? '계열사 추가를 선택해 첫 계열사를 등록해 주세요.'
                    : '기준 회사 외에 등록된 회사가 없습니다.'
                }
              >
                <Paper
                  variant="outlined"
                  sx={{
                    overflow: 'hidden',
                  }}
                >
                  <Stack divider={<Divider />}>
                    {affiliates.map((profile) => {
                      const blocked = dirty && draft?.companyId !== profile.companyId;
                      return (
                        <Stack
                          key={profile.companyId}
                          direction={{
                            xs: 'column',
                            sm: 'row',
                          }}
                          sx={{
                            alignItems: {
                              xs: 'stretch',
                              sm: 'center',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <SettingRow
                              title={profile.descriptor}
                              value={profile.descriptor}
                              description={
                                profile.registeredAddress.trim() || '법인 공식 정보와 증명서 직인을 확인합니다.'
                              }
                              icon={<AccountBalanceOutlined fontSize="small" />}
                              statusLabel={
                                dirty && draft?.companyId === profile.companyId
                                  ? '저장하지 않은 변경'
                                  : blocked
                                    ? '다른 회사 저장 대기'
                                    : undefined
                              }
                              statusTone="warning"
                              compact
                              ariaLabel={blocked ? undefined : `${profile.descriptor} 상세 열기`}
                              onClick={
                                blocked ? undefined : () => location.navigate('company-legal', profile.companyId)
                              }
                            />
                          </Box>
                          {canReadSeal ? (
                            <Box
                              sx={{
                                px: 2,
                                pb: {
                                  xs: 1.5,
                                  sm: 0,
                                },
                                flexShrink: 0,
                              }}
                            >
                              <Button
                                variant="text"
                                startIcon={<VerifiedOutlined />}
                                aria-label={`${profile.descriptor} 증명서 직인 열기`}
                                onClick={() => route.navigate('admin-company-seals', 'company-seal', profile.companyId)}
                              >
                                증명서 직인
                              </Button>
                            </Box>
                          ) : null}
                        </Stack>
                      );
                    })}
                  </Stack>
                </Paper>
              </ResourceState>
            </PageSections></Box>
          )}
        </Box>
        <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice(undefined)} />
      </InformationPage>
      {canWrite ? (
        <AffiliateCreateDialog
          open={createOpen}
          creating={creating}
          createValid={createValid}
          createDraft={createDraft}
          createProfile={createProfile}
          createError={createError}
          onClose={() => {
            if (!creating) {
              setCreateOpen(false);
              setCreateError(undefined);
            }
          }}
          onSubmit={create}
          onChange={(field, value) => setCreateDraft((current) => ({ ...current, [field]: value }))}
        />
      ) : null}
    </>
  );
}
